import React from 'react'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  PaperProps,
  useTheme,
  InputAdornment,
  Stack,
  styled,
  Box,
} from '@mui/material'
import TextField, { TextFieldProps } from './TextField'
import MaterialSymbol from './MaterialSymbol'

const EndAdornmentOverride = styled(Box)`
  .MuiAutocomplete-endAdornment {
    position: unset !important;
    transform: unset !important;
    padding-bottom: 2px;
  }
`
interface StyleProps {
  endIcon?: React.ReactNode
  alignment?: 'left' | 'right'
  label?: string
  isTopElement?: boolean
  textFieldProps?: Partial<TextFieldProps>
}
interface ComponentProps extends StyleProps {
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  testId?: string
  placeholder?: string
}

function Autocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  label,
  isTopElement,
  testId,
  textFieldProps,
  endIcon,
  ...props
}: Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> &
  ComponentProps) {
  const theme = useTheme()
  return (
    <MuiAutocomplete
      sx={{
        '&:hover fieldset': {
          border: `1px solid ${theme.colors.primary500} !important`,
        },
      }}
      {...props}
      forcePopupIcon={!endIcon}
      componentsProps={{
        popper: {
          sx: { minWidth: '300px', zIndex: isTopElement ? 1500 : 1000 },
          placement: 'bottom-start',
          ...props.componentsProps?.popper,
        },
        paper: {
          sx: { marginTop: '6px', alignSelf: 'right' },
        } as PaperProps,
        popupIndicator: {
          disableRipple: true,
        },
      }}
      popupIcon={<MaterialSymbol sx={{ width: '48px' }}>expand_more</MaterialSymbol>}
      renderInput={
        props.renderInput
          ? props.renderInput
          : (params: AutocompleteRenderInputParams): React.ReactNode => {
              return (
                <TextField
                  testId={testId}
                  label={label}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    type: textFieldProps?.type,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Stack direction='row' display='relative' alignItems={'center'}>
                        <EndAdornmentOverride>
                          {params.InputProps.endAdornment}
                        </EndAdornmentOverride>
                        <InputAdornment position='start'>{endIcon}</InputAdornment>
                      </Stack>
                    ),
                  }}
                  placeholder={props.placeholder}
                  {...textFieldProps}
                  variant='outlined'
                  sx={{ pr: `${endIcon ? 9 : 3}px !important` }}
                />
              )
            }
      }
    />
  )
}

export default Autocomplete
